import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, message } from 'antd';
import moment from 'moment';
import { getAdminAuditEventsAll } from '../../services/auditTrailService';
import AuditDetailModal from './AuditDetailModal';
import { parseChannel, channelTagColor } from './utils';

/**
 * Admin Activity Tab
 * Uses the specialized backend endpoint to fetch administrative actions.
 */
const AdminAuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchLogs = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const res = await getAdminAuditEventsAll({ page: page - 1, size });
      
      if (res?.success) {
        const data = res.data?.content || res.data || [];
        setLogs(Array.isArray(data) ? data : (data.content || []));
        setPagination({ 
          current: page, 
          pageSize: size, 
          total: res.data?.totalElements || (Array.isArray(data) ? data.length : 0) 
        });
      } else {
        const errorMsg = res?.message || 'Failed to fetch administrative events from the server.';
        message.error(errorMsg);
        setLogs([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (err) {
      console.error('Failed to fetch admin audit logs:', err);
      message.error('An unexpected error occurred while fetching admin activity.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id) => <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{id}</span>,
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (type) => (
        <Tag
          style={{
            background: '#f9f0ff',
            color: '#722ed1',
            border: '1px solid #d3adf7',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {type?.replace(/_/g, ' ') || '—'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => <span style={{ fontWeight: 500 }}>{action}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Entity',
      dataIndex: 'entityType',
      key: 'entityType',
      width: 120,
      render: (entity) => (
        <Tag style={{ background: '#f5f5f5', color: '#595959', border: '1px solid #e8e8e8', borderRadius: 6 }}>
          {entity}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const isSuccess = status === 'SUCCESS';
        return (
          <Tag
            style={{
              color: isSuccess ? '#52c41a' : '#ff4d4f',
              background: isSuccess ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${isSuccess ? '#b7eb8f' : '#ffa39e'}`,
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Channel',
      dataIndex: 'userAgent',
      key: 'channel',
      render: (ua) => {
        const channel = parseChannel(ua);
        return (
          <Tag color={channelTagColor(channel)} style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}>
            {channel}
          </Tag>
        );
      },
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm A') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="audit-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({ onClick: () => handleRowClick(record) })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'audit-row-even' : 'audit-row-odd')}
          scroll={{ x: 'max-content' }}
          className="audit-table"
        />
      </div>
      <AuditDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        record={selectedLog}
      />
    </div>
  );
};

export default AdminAuditTab;
